import { Token } from '../types/supabase';
import { userService } from './userService';
import { getUserProfile } from './userProfileService';
import { jupiterService } from './jupiterService';
// Explicitly import Buffer to ensure it's available
import { Buffer } from 'buffer';

// Global registry to track active toasts by token mint to prevent duplicates
const activeToastRegistry = new Map<string, string>();

// Global flag to track if we're in the middle of processing
let isProcessingAnyTransaction = false;

// Global variable to track the currently-being-processed token
let currentProcessingToken: string | null = null;

// Use a unique ID for transaction tracking
let lastTransactionId = 0;

// Debug mode (set to false in production)
const DEBUG = false;

/**
 * Show a toast notification with the given message, type, and optional ID
 * Only shows toast if there isn't already one for this token
 */
const showToast = (message: string, type: 'success' | 'error' | 'loading', tokenMint: string = '', id?: string) => {
    // Generate a unique ID if not provided
    const toastId = id || `toast-${type}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // If we have a token mint, check if there's already a toast for this token
    if (tokenMint) {
        const existingToastId = activeToastRegistry.get(tokenMint);
        if (existingToastId) {
            if (DEBUG) console.log(`[QuickBuy] Toast already exists for ${tokenMint}, updating it: ${message}`);

            // Update the existing toast instead of creating a new one
            window.dispatchEvent(new CustomEvent('ekko:update_toast', {
                detail: {
                    id: existingToastId,
                    message,
                    type
                }
            }));
            return existingToastId; // Return the existing ID
        }

        // Register this toast with the token
        activeToastRegistry.set(tokenMint, toastId);

        // Set a safety timeout to clear this toast registration after 30 seconds
        // This prevents "stuck" toasts if an error occurs and cleanup is missed
        setTimeout(() => {
            if (activeToastRegistry.get(tokenMint) === toastId) {
                if (DEBUG) console.log(`[QuickBuy] Safety cleanup of toast registry for ${tokenMint}`);
                activeToastRegistry.delete(tokenMint);
            }
        }, 30000);
    }

    if (DEBUG) console.log(`[QuickBuy] SHOWING TOAST (${type}): ${message} ${toastId ? `[ID: ${toastId}]` : ''}`);

    window.dispatchEvent(new CustomEvent('ekko:show_toast', {
        detail: {
            message,
            type,
            id: toastId,
            tokenMint: tokenMint || undefined // Only pass if non-empty
        }
    }));

    return toastId;
};

/**
 * Hide a toast notification with the given ID and remove from registry
 */
const hideToast = (id: string, tokenMint: string = '', type?: 'success' | 'error' | 'loading') => {
    if (DEBUG) console.log(`[QuickBuy] HIDING TOAST: ${id} ${type ? `[Type: ${type}]` : ''}`);

    // Remove from registry if this is the registered toast for this token
    if (tokenMint && activeToastRegistry.get(tokenMint) === id) {
        activeToastRegistry.delete(tokenMint);
    }

    window.dispatchEvent(new CustomEvent('ekko:hide_toast', {
        detail: {
            id,
            type
        }
    }));
};

/**
 * Clear all toasts of a specific type or all toasts if no type is provided
 */
const clearToasts = (type?: 'success' | 'error' | 'loading') => {
    if (DEBUG) console.log(`[QuickBuy] CLEARING ALL TOASTS ${type ? `of type: ${type}` : ''}`);

    // Clear the registry when clearing all toasts
    activeToastRegistry.clear();

    window.dispatchEvent(new CustomEvent('ekko:clear_toasts', {
        detail: { type }
    }));
};

/**
 * Get user's quick buy preferences
 * @param walletAddress The user's wallet address
 * @returns Quick buy preferences
 */
export const getQuickBuyPreferences = async (walletAddress: string) => {
    try {
        // Get user data from Supabase
        const userData = await userService.getUserByWallet(walletAddress);

        // Default values to use if nothing is found
        const defaults = {
            amount: '0.01',  // Default amount
            slippage: '20'   // Default 20% slippage
        };

        // If user data exists and has preferences 
        if (userData && userData.user_preferences) {
            const userPrefs = userData.user_preferences;

            console.log(`🔍 [QuickBuy] Raw user_preferences from database:`, JSON.stringify(userPrefs, null, 2));
            console.log(`🔍 [QuickBuy] Looking for quickBuyAmount: ${userPrefs.quickBuyAmount}`);
            console.log(`🔍 [QuickBuy] Looking for quickBuySlippage: ${userPrefs.quickBuySlippage}`);
            console.log(`🔍 [QuickBuy] All available keys:`, Object.keys(userPrefs));

            // Look for quickBuySlippage first (global setting)
            if (userPrefs.quickBuySlippage) {
                console.log(`[QuickBuy] Using global slippage from Supabase: ${userPrefs.quickBuySlippage}%`);
            }

            // Return profile preferences with fallbacks to defaults
            // Try multiple possible field names for amount
            const amount = userPrefs.quickBuyAmount ||
                userPrefs.quickbuy_amount ||
                userPrefs.quick_buy_amount ||
                defaults.amount;

            // Try multiple possible field names for slippage  
            const slippage = userPrefs.quickBuySlippage ||
                userPrefs.quickbuy_slippage ||
                userPrefs.quick_buy_slippage ||
                userPrefs.slippage ||
                defaults.slippage;

            const result = {
                amount: amount,
                slippage: slippage
            };

            console.log(`🔍 [QuickBuy] Final preferences result:`, result);
            return result;
        }

        // If no Supabase preferences, check localStorage
        if (typeof window !== 'undefined') {
            // Try to load from localStorage
            const localPrefs = localStorage.getItem('ekko_user_preferences');
            if (localPrefs) {
                try {
                    const parsedPrefs = JSON.parse(localPrefs);
                    // Return localStorage preferences with fallbacks to defaults
                    return {
                        amount: parsedPrefs.quickBuyAmount || defaults.amount,
                        slippage: parsedPrefs.quickBuySlippage || defaults.slippage
                    };
                } catch (e) {
                    console.error('[QuickBuy] Error parsing localStorage preferences:', e);
                }
            }

            // Also check individual localStorage keys (legacy support)
            const savedAmount = localStorage.getItem('ekko_quick_buy_amount');
            const savedSlippage = localStorage.getItem('ekko_quick_buy_slippage');

            if (savedAmount || savedSlippage) {
                return {
                    amount: savedAmount || defaults.amount,
                    slippage: savedSlippage || defaults.slippage
                };
            }
        }

        // Return defaults if nothing found
        return defaults;
    } catch (error) {
        console.error('[QuickBuy] Error getting quick buy preferences:', error);
        return {
            amount: '0.01',  // Default amount
            slippage: '20'   // Default 20% slippage
        };
    }
};

/**
 * Execute a quick buy trade using saved user preferences
 * @param token The token to buy
 * @param walletAddress The user's wallet address
 * @param customSettings Optional custom settings to override saved preferences
 * @returns Promise with the transaction result
 */
export const executeQuickBuy = async (
    token: Token,
    walletAddress: string,
    customSettings?: {
        amount?: string;
        slippage?: string;
    }
) => {
    // Validate token and mint - mint should be required according to type definition
    if (!token?.mint) {
        showToast('No token provided for quick buy', 'error', '');
        return { success: false, error: 'No token provided' };
    }

    if (!walletAddress) {
        showToast('Wallet address is required', 'error', '');
        return { success: false, error: 'No wallet address' };
    }

    // Track this specific transaction with an ID
    const transactionId = ++lastTransactionId;
    if (DEBUG) console.log(`[QuickBuy] Starting transaction #${transactionId} for ${token.symbol || token.mint}`);

    // Ensure we're not processing another transaction
    if (isProcessingAnyTransaction) {
        if (DEBUG) console.log(`[QuickBuy] Already processing transaction #${transactionId - 1}, rejecting #${transactionId}`);
        showToast('Please wait until the current transaction completes', 'error', token.mint);
        return { success: false, error: 'Already processing' };
    }

    try {
        // Set processing flags
        isProcessingAnyTransaction = true;
        currentProcessingToken = token.mint;

        // Create a unique ID for this token's loading toast
        const toastId = showToast('Checking wallet and preferences...', 'loading', token.mint);

        // Get user preferences or use custom settings if provided
        let preferences;
        if (customSettings) {
            // Start with defaults
            preferences = {
                amount: '0.01',
                slippage: '20'
            };

            // Get saved preferences
            const savedPreferences = await getQuickBuyPreferences(walletAddress);

            // Merge saved preferences
            preferences = {
                amount: savedPreferences.amount,
                slippage: savedPreferences.slippage
            };

            // Override with custom settings if provided
            if (customSettings.amount) {
                preferences.amount = customSettings.amount;
            }

            if (customSettings.slippage) {
                preferences.slippage = customSettings.slippage;
            }

            if (DEBUG) console.log(`[QuickBuy] Using custom settings: amount=${preferences.amount}, slippage=${preferences.slippage}`);
        } else {
            // Just use saved preferences
            preferences = await getQuickBuyPreferences(walletAddress);
        }

        // Update loading message with token information
        showToast(`Buying ${token.symbol || token.name || 'token'} with ${preferences.amount} SOL...`, 'loading', token.mint, toastId);

        try {
            // Use jupiterService.quickBuyToken instead of direct buySolToken call
            // This will handle getting the active trading wallet and showing toast notifications
            const result = await jupiterService.quickBuyToken({
                tokenMint: token.mint,
                amount: parseFloat(preferences.amount),
                slippage: parseFloat(preferences.slippage)
            });

            // Hide loading toast since jupiterService will have shown its own toast
            hideToast(toastId, token.mint, 'loading');

            if (!result.success) {
                showToast(`Quick buy failed: ${result.error}`, 'error', token.mint);
                if (DEBUG) console.log(`[QuickBuy] Error executing quick buy: ${result.error}`);
                return { error: result.error, success: false };
            }

            // Show success toast
            showToast(`Successfully bought ${token.symbol || token.name || 'token'}!`, 'success', token.mint);

            if (DEBUG) console.log(`[QuickBuy] Transaction #${transactionId} completed successfully`);
            return { signature: result.txId, success: true };
        } catch (error) {
            // Extract error message without logging the entire error
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';

            // Hide loading toast and show a single error toast
            hideToast(toastId, token.mint, 'loading');
            showToast(`Quick buy failed: ${errorMessage}`, 'error', token.mint);

            if (DEBUG) console.log(`[QuickBuy] Error executing quick buy: ${errorMessage}`);
            return { error: errorMessage, success: false };
        }
    } finally {
        // Always clean up processing state
        if (currentProcessingToken === token.mint) {
            currentProcessingToken = null;
        }
        isProcessingAnyTransaction = false;

        if (DEBUG) console.log(`[QuickBuy] Transaction #${transactionId} processing completed`);
    }
};

// Track event listener setup to prevent multiple listeners
let isEventListenerSetup = false;

/**
 * Set up an event listener for quick buy events
 */
export const setupQuickBuyEventListener = () => {
    // Only set up once to prevent duplicates
    if (isEventListenerSetup) {
        if (DEBUG) console.log('[QuickBuy] Event listener already set up, skipping');
        return;
    }

    // Clean slate - remove any previous event listeners that might be registered
    window.removeEventListener('ekko:quick_buy', handleQuickBuyEvent as EventListener);

    // Debounced event tracking
    let lastEventTime = 0;
    const EVENT_DEBOUNCE_TIME = 1000; // 1 second minimum between events

    // The actual event handler function - defined as a named function so we can remove it later
    async function handleQuickBuyEvent(event: any) {
        // Simple debouncing to prevent rapid-fire events
        const now = Date.now();
        if (now - lastEventTime < EVENT_DEBOUNCE_TIME) {
            if (DEBUG) console.log(`[QuickBuy] Event received too quickly, ignoring (${now - lastEventTime}ms)`);
            return;
        }
        lastEventTime = now;

        if (DEBUG) console.log(`[QuickBuy] Event received at ${new Date().toISOString()}`);

        // If we're already processing a transaction, ignore this request
        if (isProcessingAnyTransaction) {
            if (DEBUG) console.log(`[QuickBuy] Already processing a transaction, ignoring this request`);
            showToast('Already processing a transaction. Please wait.', 'error', '');
            return;
        }

        const { token } = event.detail;

        if (!token || !token.mint) {
            showToast('Quick buy failed: No token data provided', 'error', '');
            return;
        }

        try {
            // Try to get wallet address from several sources
            let walletAddress = '';

            // 1. Try from localStorage first
            walletAddress = localStorage.getItem('walletAddress') || '';

            // 2. If not found, try from connected wallets
            if (!walletAddress) {
                // Check Phantom wallet
                if (window.solana?.isConnected && window.solana?.publicKey) {
                    walletAddress = window.solana.publicKey.toString();
                }
                // Check Solflare wallet
                else if (window.solflare?.isConnected && window.solflare?.publicKey) {
                    walletAddress = window.solflare.publicKey.toString();
                }
            }

            if (!walletAddress) {
                showToast('Please connect your wallet to use Quick Buy', 'error', '');
                return;
            }

            // Execute the quick buy with the found wallet address
            await executeQuickBuy(token, walletAddress);
        } catch (error) {
            // This is a last-resort catch for any unhandled errors
            // Normally errors should be caught in executeQuickBuy
            if (DEBUG) console.log(`[QuickBuy] Unhandled error in event handler:`, error);

            // Reset processing state
            isProcessingAnyTransaction = false;
            currentProcessingToken = null;

            // Show a generic error
            showToast('An unexpected error occurred', 'error', '');
        }
    }

    // Register our handler
    window.addEventListener('ekko:quick_buy', handleQuickBuyEvent as EventListener);
    isEventListenerSetup = true;

    if (DEBUG) console.log('[QuickBuy] Event listener setup complete');
};

/**
 * Initialize quick buy service and set up event listeners for real-time updates
 */
export function initializeQuickBuyService() {
    // Add event listener for quick buy settings changes
    window.addEventListener('ekko:quick_buy_amount_changed', async (event: Event) => {
        const customEvent = event as CustomEvent<{ amount: number, slippage?: string }>;
        console.log(`Quick buy amount changed to ${customEvent.detail.amount}, slippage: ${customEvent.detail.slippage || 'using existing'}`);

        // Try to update local cache right away for immediate visual feedback
        try {
            const walletAddress = localStorage.getItem('walletAddress') || '';

            // Properly await the async function
            const currentSettings = await getQuickBuyPreferences(walletAddress);

            // Get slippage from event if provided, otherwise preserve existing setting
            // Default to 20% if nothing is available
            const slippageToUse = customEvent.detail.slippage ||
                currentSettings.slippage ||
                '20';

            // Now we can safely access the properties
            const updatedSettings = {
                amount: customEvent.detail.amount.toString(),
                slippage: slippageToUse
            };

            console.log(`Updating quick buy settings: amount=${updatedSettings.amount}, slippage=${updatedSettings.slippage}`);

            // Update localStorage for immediate access
            localStorage.setItem('ekko_quick_buy_amount', updatedSettings.amount);
            localStorage.setItem('ekko_quick_buy_slippage', updatedSettings.slippage);
        } catch (error) {
            console.error('Error updating quick buy settings from event:', error);
        }
    });

    // Initialize any other listeners or setup needed
}

// Call initialization when module is loaded
if (typeof window !== 'undefined') {
    initializeQuickBuyService();
}

/**
 * Get the quick buy amount from localStorage for immediate UI display
 * This is a synchronous version that doesn't require awaiting
 * 
 * @returns The current quick buy amount as a number
 */
export function getCachedQuickBuyAmount(): number {
    try {
        const cachedAmount = localStorage.getItem('ekko_quick_buy_amount');
        if (cachedAmount) {
            const parsedAmount = parseFloat(cachedAmount);
            return isNaN(parsedAmount) ? 0.1 : parsedAmount;
        }
        return 0.1; // Default amount
    } catch (error) {
        console.error('Error getting cached quick buy amount:', error);
        return 0.1; // Default on error
    }
} 